import { useEffect, useState } from "react";
import { useSelector } from "@tanstack/react-store";
import { Button, Icon, Stack } from "@bookingcom/bui-react";
import SettingsIcon from "@bookingcom/bui-assets-react/streamline/SettingsIcon";
import { useI18n } from "@bookingcom/lingojs-react";
import { localStorageConfigStore } from "../../stores/localStorageConfigStore";
import type { Environment } from "../../types";
import { buildUrlWithFeatureOverrides } from "../../utils/features";
import { FeatureOverrideSheet } from "./FeatureOverrideSheet";
import { DevToolsPanelContent } from "./DevToolsPanelContent";
import styles from "./DevToolsPanel.module.css";

const margin = "var(--bui_spacing_4x)";
const positions = [
  { bottom: margin, left: margin },
  { top: margin, left: margin },
  { top: margin, right: margin },
  { bottom: margin, right: margin },
] as const;

export type DevToolsPanelProps = {
  environment: Environment;
};

export const DevToolsPanel = ({ environment }: DevToolsPanelProps) => {
  const i18n = useI18n();
  const localStorageConfig = useSelector(localStorageConfigStore, (state) => state);
  const [isOpen, setIsOpen] = useState(false);
  const [isFeatureOverrideOpen, setIsFeatureOverrideOpen] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(0);

  useEffect(() => {
    i18n.setShowTags(!!localStorageConfig.psmConfigShowCopyTags);
  }, [i18n, localStorageConfig]);

  return (
    <>
      <div className={styles.devToolsPanel} style={positions[currentPosition]}>
        {isOpen && (
          <DevToolsPanelContent
            environment={environment}
            setIsOpen={setIsOpen}
            onOpenFeatureOverride={() => setIsFeatureOverrideOpen(true)}
          />
        )}
        {!isOpen && (
          <Stack direction="row" justifyContent="end">
            <Button
              variant="white"
              className={styles.gearButton}
              icon={<Icon svg={SettingsIcon} color="callout" />}
              ariaLabel="Open developer tools"
              onClick={(e) => {
                if ("shiftKey" in e && e.shiftKey) {
                  setCurrentPosition((index) => (index + 1) % positions.length);
                  return;
                }
                setIsOpen((prev) => !prev);
              }}
            />
          </Stack>
        )}
      </div>
      {isFeatureOverrideOpen && (
        <FeatureOverrideSheet
          active
          features={environment.features}
          onClose={() => setIsFeatureOverrideOpen(false)}
          onApply={(overrides) => window.location.replace(buildUrlWithFeatureOverrides(overrides).toString())}
        />
      )}
    </>
  );
};
